import type { Message } from '../../types/mfc'
import { getRoomState, setRoomMessagesState } from '../app-state/state.room'
import { UserAction } from '../constants/enums'
import { ManageListsType, manageListsApi } from '../mfcLogic/api/lists'
import { resolveID } from '../state/state.users'
import { loadUserIfNeeded } from './loadUserIfNeeded'
import { hidePM } from './message'
import { userAction } from './workerActions'

export const loadUserExtras = (identifier: string | number) => {
    userAction(UserAction.LOAD_EXTRAS, { identifier })
}

export const removeBookmark = (identifier: string | number) => {
    const id = resolveID(identifier)
    if (!id) return
    userAction(UserAction.REMOVE_BOOKMARK, { id })
}

export const removeFriend = (identifier: string | number) => {
    const id = resolveID(identifier)
    if (!id) return
    userAction(UserAction.REMOVE_FRIEND, { id })
}

export const addFriend = async (slugOrId: string | number) => {
    const user = await loadUserIfNeeded(slugOrId)

    if (!user) return

    const { id, isFriend, isBookmark, slug } = user
    const { [id]: room } = getRoomState()

    if (room?.isOpen && !isFriend) {
        // show the in chat notification if in the room and adding a friend
        addFriendBookmarkMessageToRoom(slug, {
            isBookmark,
            isFriend: true,
        })
    }

    userAction(UserAction.ADD_FRIEND, { id })
}

export const bookmarkModel = async (slugOrId: string | number) => {
    const user = await loadUserIfNeeded(slugOrId)

    if (!user) return

    const { id, isBookmark, isFriend, slug } = user
    const { [id]: room } = getRoomState()

    if (room?.isOpen && !isBookmark) {
        // show the in chat notification if in the room and adding a bookmark
        addFriendBookmarkMessageToRoom(slug, {
            isBookmark: true,
            isFriend,
        })
    }

    userAction(UserAction.ADD_BOOKMARK, { id })
}

export const ignoreUser = async (slugOrId: string | number) => {
    const user = await loadUserIfNeeded(slugOrId)
    if (!user) return
    const { id, isIgnored } = user
    const action = isIgnored ? UserAction.REMOVE_IGNORE : UserAction.ADD_IGNORE
    userAction(action, { id })
    manageListsApi(ManageListsType.ignores, [id], { remove: isIgnored })
    hidePM(id)
}

export const recoverFromKick = (id: number) => {
    userAction(UserAction.RECOVER_FROM_KICK, { id })
}

// export const setModelYouMayLikeTarget = async (uid: number) => {
//     userAction(UserAction.SET_MODELS_YOU_MAY_LIKE_TARGET, uid)
// }

export const hideModel = async (slugOrId: string | number) => {
    const user = await loadUserIfNeeded(slugOrId)
    if (!user) return
    const { id, isHidden } = user
    const action = isHidden ? UserAction.UNHIDE_USER : UserAction.HIDE_USER
    userAction(action, { id })
    manageListsApi(ManageListsType.hidden, [id], { remove: isHidden })
}

export const ignoreModel = async (slugOrId: string | number) => {
    const user = await loadUserIfNeeded(slugOrId)
    if (!user) return
    const { id, isIgnored } = user
    const action = isIgnored ? UserAction.REMOVE_IGNORE : UserAction.ADD_IGNORE
    userAction(action, { id })
    manageListsApi(ManageListsType.ignores, [id], { remove: isIgnored })
}

export const modelGetSocialMedia = async (slugOrId: string | number) => {
    const user = await loadUserIfNeeded(slugOrId)
    if (!user) return
    const { id, instagram, twitter } = user
    if (instagram || twitter) return
    userAction(UserAction.LOAD_SOCIAL_MEDIA, { id })
}

async function addFriendBookmarkMessageToRoom(
    slugOrId: string | number,
    newStatus: { isBookmark: boolean; isFriend: boolean },
) {
    const model = await loadUserIfNeeded(slugOrId)
    if (!model) return
    const { isBookmark, isFriend } = newStatus
    const { name, id } = model
    const message: Message = { text: '', system: true }
    if (isBookmark && !isFriend) {
        message.text = `${name} added as a Bookmark`
    } else if (isBookmark && isFriend) {
        message.text = `${name} is now a Bookmarked Friend`
    } else if (isFriend && !isBookmark) {
        message.text = `${name} added as a Friend`
    }

    setRoomMessagesState(message, id)
}
